import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { VariantFormData } from '../types';

interface VariantDialogProps {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: VariantFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<VariantFormData>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  isEditing: boolean;
}

export function VariantDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  isSubmitting,
  isEditing,
}: VariantDialogProps) {
  const inputClass = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelClass = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-lg', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            {isEditing ? 'Изменить вариант' : 'Новый ценовой вариант'}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            Вариант — это весь прайс-лист целиком: и цены, и состав тарифов.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className={labelClass}>Название *</Label>
            <Input
              value={formData.name}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Дешёвый / Контроль / Дорогой"
              className={inputClass}
            />
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Вес набора</Label>
            <Input
              type="number"
              min={0}
              value={formData.weight}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, weight: parseInt(e.target.value) || 0 }))
              }
              className={inputClass}
            />
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              Не проценты, а вес: 40 / 40 / 20 даст доли 40%, 40% и 20%. Вес 0 —
              вариант в набор не попадёт.
            </p>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Описание</Label>
            <Textarea
              value={formData.description}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Гипотеза и планируемая дата остановки — автозавершения нет, тест останавливают вручную"
              rows={3}
              className={inputClass}
            />
          </div>

          <div className="flex items-start gap-3 pt-1">
            <Switch
              checked={formData.is_control}
              onCheckedChange={(checked) =>
                onFormDataChange((prev) => ({ ...prev, is_control: checked }))
              }
            />
            <div>
              <Label className={labelClass}>Контрольный вариант</Label>
              <p className={cn('text-xs mt-1', isDark ? 'text-slate-500' : 'text-slate-400')}>
                Текущие цены. На него переводят назначенных при архивировании
                других вариантов, поэтому контрольный — ровно один.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
